import React, { useEffect, useState } from 'react';
import { Package } from 'lucide-react';
import { adminFetchJson } from '../../lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const gbp = (v) => (typeof v === 'number' ? `£${v.toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—');

const addressLines = (o) => {
  const a = o.address || {};
  return [o.ship_to_name, a.line1, a.line2, [a.city, a.state].filter(Boolean).join(', '), a.postal_code, a.country]
    .filter(Boolean);
};

const OrderCard = ({ o }) => {
  const lines = addressLines(o);
  return (
    <div className="border border-gray-200 rounded-lg p-4 grid md:grid-cols-3 gap-4 text-sm" data-testid={`paid-order-${o.session_id}`}>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Bought</p>
        <p className="font-medium" data-testid="paid-order-item">{o.artwork_title || 'Untitled'}</p>
        <p className="text-gray-500">{o.artist_name}{o.size ? ` · ${o.size}` : ''}</p>
        <p className="mt-1">{gbp(o.amount)}
          <span className={`ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${o.shipping_paid ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
            {o.shipping_paid ? 'Shipping paid' : 'Shipping not paid'}
          </span>
        </p>
        <p className="text-xs text-gray-400 mt-1">{o.date ? new Date(o.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Client</p>
        <p className="font-medium" data-testid="paid-order-buyer-name">{o.buyer_name || '—'}</p>
        <a href={`mailto:${o.buyer_email}`} className="text-gray-600 underline break-all" data-testid="paid-order-buyer-email">{o.buyer_email}</a>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Ship to</p>
        {lines.length ? (
          <address className="not-italic leading-snug" data-testid="paid-order-address">
            {lines.map((l) => <div key={l}>{l}</div>)}
          </address>
        ) : (
          <p className="text-gray-400" data-testid="paid-order-address">No address recorded</p>
        )}
      </div>
    </div>
  );
};

const PaidOrders = () => {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    adminFetchJson(`${BACKEND_URL}/api/admin/payments/orders`)
      .then((d) => setOrders(d.orders || []))
      .catch(() => setOrders([]));
  }, []);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2" data-testid="paid-orders-panel">
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-5 h-5" />
        <h3 className="font-semibold">Paid orders</h3>
        {orders && <span className="text-xs text-gray-400">({orders.length})</span>}
      </div>
      {orders === null && <p className="text-sm text-gray-400">Loading…</p>}
      {orders && !orders.length && <p className="text-sm text-gray-400" data-testid="paid-orders-empty">No paid orders yet.</p>}
      <div className="space-y-3 max-h-[640px] overflow-y-auto">
        {(orders || []).map((o) => <OrderCard key={o.session_id} o={o} />)}
      </div>
    </div>
  );
};

export default PaidOrders;
